import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { invalidateEntity } from '@/api/cache-invalidation';
import { tenantKeys } from '@/pages/admin/api/queries/useTenants';
import { seatUsageKeys } from '@/api/query-keys';
import { usePendingRowIds } from '@/api/mutations/usePendingRowIds';
import {
  addTenantUserContract,
  type AddTenantUserRequest,
  type AddTenantUserResponse,
  updateTenantUserContract,
  type UpdateTenantUserRequest,
  type UpdateTenantUserResponse,
  sendTenantUserInvitationContract,
  type SendTenantUserInvitationResponse,
  generateTenantUserActivationLinkContract,
  type GenerateTenantUserActivationLinkResponse,
  deleteTenantUserContract,
  type DeleteTenantUserResponse,
  resetTenantUserSignInContract,
  type ResetIdentitySignInResponse
} from '@bliks-io/contracts';

/**
 * Add tenant user mutation
 *
 * Creates a new user within a specific tenant (super-admin only).
 * - Invalidates tenant users list cache
 * - Returns the newly created user
 *
 * @example
 * ```tsx
 * function AddTenantUserForm() {
 *   const addTenantUser = useAddTenantUser();
 *
 *   const handleSubmit = async (data: AddTenantUserRequest) => {
 *     try {
 *       const result = await addTenantUser.mutateAsync(data);
 *       console.log('Created tenant user:', result.tenantUser);
 *       toast.success('User added successfully');
 *     } catch (error) {
 *       toast.error('Failed to add user');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Button loading={addTenantUser.isPending}>Add User</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useAddTenantUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenantUserData: AddTenantUserRequest): Promise<AddTenantUserResponse> => {
      const { data } = await apiClient.post<AddTenantUserResponse>(
        addTenantUserContract.path,
        tenantUserData
      );
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate tenant users list to refetch with new user
      queryClient.invalidateQueries({ queryKey: tenantKeys.users(variables.tenantId) });
      queryClient.invalidateQueries({ queryKey: seatUsageKeys.tenant(variables.tenantId) });
    }
  });
}

/**
 * Update tenant user mutation
 *
 * Updates an existing tenant user by ID (super-admin only).
 * - Invalidates tenant users list cache
 * - Returns the updated user
 *
 * @example
 * ```tsx
 * function EditTenantUserForm({ tenantId, userId }: { tenantId: string; userId: string }) {
 *   const updateUser = useUpdateTenantUser(tenantId);
 *
 *   const handleSubmit = async (data: UpdateTenantUserRequest) => {
 *     try {
 *       await updateUser.mutateAsync({ id: userId, data });
 *       toast.success('User updated successfully');
 *     } catch (error) {
 *       toast.error('Failed to update user');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Button loading={updateUser.isPending}>Update User</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useUpdateTenantUser(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdateTenantUserRequest;
    }): Promise<UpdateTenantUserResponse> => {
      const path = updateTenantUserContract.path
        .replace(':tenantId', tenantId)
        .replace(':userId', id);
      const { data: response } = await apiClient.patch<UpdateTenantUserResponse>(path, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) });
      invalidateEntity(queryClient, 'user');
      // A role change moves the user between seat pools.
      queryClient.invalidateQueries({ queryKey: seatUsageKeys.tenant(tenantId) });
    }
  });
}

/** Ids of the tenant users with an invitation or activation-link request in flight. */
export function usePendingTenantUserIds(tenantId: string): ReadonlySet<string> {
  return usePendingRowIds(
    tenantKeys.userRowMutations(tenantId),
    (variables) => (variables as { userId: string }).userId
  );
}

/**
 * Send tenant user invitation mutation
 *
 * Sends an invitation email to a tenant user (super-admin only).
 * - Invalidates tenant users list cache
 * - Returns the updated user with pending status
 *
 * @example
 * ```tsx
 * function TenantUserActions({ tenantId, userId }: { tenantId: string; userId: string }) {
 *   const sendInvitation = useSendTenantUserInvitation(tenantId);
 *
 *   const handleSendInvitation = async () => {
 *     try {
 *       await sendInvitation.mutateAsync({ userId });
 *       toast.success('Invitation sent successfully');
 *     } catch (error) {
 *       toast.error('Failed to send invitation');
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleSendInvitation} loading={sendInvitation.isPending}>
 *       Send Invitation
 *     </Button>
 *   );
 * }
 * ```
 */
export function useSendTenantUserInvitation(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: tenantKeys.sendUserInvitationMutation(tenantId),
    mutationFn: async ({
      userId
    }: {
      userId: string;
    }): Promise<SendTenantUserInvitationResponse> => {
      const path = sendTenantUserInvitationContract.path
        .replace(':tenantId', tenantId)
        .replace(':userId', userId);
      const { data: response } = await apiClient.post<SendTenantUserInvitationResponse>(path);
      return response;
    },
    // onSettled, not onSuccess: the status flips server-side before the mail
    // dispatch, so a failed dispatch must still refresh user + seat caches.
    // Only the list is returned, so the row stays busy until the refetched list
    // shows the new status; the seat usage refreshes in the background.
    onSettled: () => {
      // An invitation claims a seat.
      queryClient.invalidateQueries({ queryKey: seatUsageKeys.tenant(tenantId) });
      return queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) });
    }
  });
}

/**
 * Generate tenant user activation link mutation
 *
 * Mints a fresh activation link for a tenant user (super-admin only) so it can be
 * delivered manually when the invitation email was lost to spam. Rotating the token
 * invalidates any previously emailed link and sets the user to pending status.
 * - Invalidates tenant users list cache (status may change to pending)
 * - Returns the updated user plus the activation link to copy
 */
export function useGenerateTenantUserActivationLink(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: tenantKeys.generateUserActivationLinkMutation(tenantId),
    mutationFn: async ({
      userId
    }: {
      userId: string;
    }): Promise<GenerateTenantUserActivationLinkResponse> => {
      const path = generateTenantUserActivationLinkContract.path
        .replace(':tenantId', tenantId)
        .replace(':userId', userId);
      const { data: response } =
        await apiClient.post<GenerateTenantUserActivationLinkResponse>(path);
      return response;
    },
    // onSettled, not onSuccess: the status flips server-side before fallible
    // late steps, so a failed request must still refresh user + seat caches.
    // Not returned: the caller's clipboard write needs the click's user
    // activation, which browsers expire before a refetch would finish.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) });
      // Minting a link flips the user to pending, which claims a seat.
      queryClient.invalidateQueries({ queryKey: seatUsageKeys.tenant(tenantId) });
    }
  });
}

/**
 * Reset tenant user sign-in mutation
 *
 * Replaces the person's sign-in account and emails them a setup link
 * (super-admin only). Every session they hold ends.
 * - Invalidates tenant users list cache
 */
export function useResetTenantUserSignIn(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }): Promise<ResetIdentitySignInResponse> => {
      const path = resetTenantUserSignInContract.path
        .replace(':tenantId', tenantId)
        .replace(':userId', userId);
      const { data: response } = await apiClient.post<ResetIdentitySignInResponse>(path);
      return response;
    },
    // onSettled, not onSuccess: the identity is reset server-side before
    // fallible late steps, so a failed request must still refresh the list.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) });
    }
  });
}

/**
 * Delete tenant user mutation
 *
 * Deletes a tenant user by ID (super-admin only).
 * - Invalidates tenant users list cache
 *
 * @example
 * ```tsx
 * function DeleteTenantUserButton({ tenantId, userId }: { tenantId: string; userId: string }) {
 *   const deleteUser = useDeleteTenantUser(tenantId);
 *
 *   const handleDelete = async () => {
 *     if (!confirm('Are you sure?')) return;
 *
 *     try {
 *       await deleteUser.mutateAsync(userId);
 *       toast.success('User deleted successfully');
 *     } catch (error) {
 *       toast.error('Failed to delete user');
 *     }
 *   };
 *
 *   return (
 *     <Button
 *       variant="destructive"
 *       loading={deleteUser.isPending}
 *       onClick={handleDelete}
 *     >
 *       Delete
 *     </Button>
 *   );
 * }
 * ```
 */
export function useDeleteTenantUser(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string): Promise<DeleteTenantUserResponse> => {
      const path = deleteTenantUserContract.path
        .replace(':tenantId', tenantId)
        .replace(':userId', userId);
      const { data } = await apiClient.delete<DeleteTenantUserResponse>(path);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.users(tenantId) });
      invalidateEntity(queryClient, 'user');
      queryClient.invalidateQueries({ queryKey: seatUsageKeys.tenant(tenantId) });
      // Note: No detail query for individual tenant users, so no removeQueries needed
    }
  });
}
